import { Badge } from './ui/badge';
import { REASON_CODE_LABELS } from '../api/checks';
import type { ReasonCode } from '../api/checks';
import { titleCase } from '../utils/format';

/**
 * Every status label in the app renders through this module (BOT-392).
 *
 * The review's "Christmas tree" complaint was that the same concept appeared as
 * plain coloured text in one table, a pill in another, lowercase here and
 * SHOUTING there. Centralising the mapping is what actually keeps them
 * consistent — a shared Badge component alone would not have.
 */

/** SAFE / UNSAFE check classification. */
export function CheckStatusBadge({ status }: { status: string }) {
  const unsafe = status === 'unsafe';
  return (
    <Badge
      variant={unsafe ? 'danger' : 'success'}
      dot={unsafe ? 'bg-red-600' : 'bg-green-500'}
    >
      {titleCase(status)}
    </Badge>
  );
}

const REVIEW_STATUS_VARIANT: Record<string, 'neutral' | 'success' | 'danger' | 'warning' | 'info'> = {
  pending:       'neutral',
  approved:      'success',
  auto_approved: 'success',
  created:       'success',
  skipped:       'warning',
  // BOT-434: "Skip for now" — frozen like skipped, but sky so it never reads as forever.
  postponed:     'info',
  disabled:      'neutral',
  create_failed: 'danger',
  // Home Value lane (BOT-411): rejected = operator kept the current value,
  // cancelled = a recompute retracted the proposal (NS already matched, or a
  // newer scrape proposed something else).
  rejected:      'warning',
  cancelled:     'neutral',
  // BOT-430: a stock-rule verdict (vendor_stock <= auto_skip_max), not an
  // operator decision — kept visually distinct (neutral slate) from the
  // amber "skipped" an operator chose deliberately.
  auto_skipped:  'neutral',
};

const REVIEW_STATUS_DOT: Record<string, string> = {
  pending:       'bg-zinc-400',
  approved:      'bg-green-500',
  auto_approved: 'bg-green-500',
  created:       'bg-green-500',
  skipped:       'bg-amber-500',
  postponed:     'bg-sky-500',
  disabled:      'bg-zinc-400',
  create_failed: 'bg-red-600',
  rejected:      'bg-amber-500',
  cancelled:     'bg-zinc-400',
  auto_skipped:  'bg-slate-400',
};

// Override for statuses where titleCase's underscore→space rendering doesn't
// match the label operators expect (BOT-430: "Auto skipped" reads as a typo
// next to "Auto-approved" / "Skipped" — the hyphen matches the filter option
// label in NewItemsProcessedPage).
const REVIEW_STATUS_LABEL: Record<string, string> = {
  auto_skipped: 'Auto-skipped',
  postponed:    'Skipped for now',
};

// BOT-434: only the New Items lane splits operator skip in two. Price-check
// history shares ReviewStatusBadge, where `skipped` is NOT "forever".
const NEW_ITEM_STATUS_LABEL: Record<string, string> = {
  skipped: 'Skipped forever',
};

export function ReviewStatusBadge(
  { status, labels }: { status: string; labels?: Record<string, string> },
) {
  return (
    <Badge variant={REVIEW_STATUS_VARIANT[status] ?? 'neutral'} dot={REVIEW_STATUS_DOT[status]}>
      {labels?.[status] ?? REVIEW_STATUS_LABEL[status] ?? titleCase(status)}
    </Badge>
  );
}

/** New Items lane: "Skipped for now" (postponed) / "Skipped forever" (skipped). */
export function NewItemReviewStatusBadge({ status }: { status: string }) {
  return <ReviewStatusBadge status={status} labels={NEW_ITEM_STATUS_LABEL} />;
}

/**
 * Reason codes are all neutral by design.
 *
 * They previously carried a different tint each (green, red, orange, blue,
 * purple, sky, indigo, rose, teal, yellow), which made a page of reasons read
 * as a page of alerts. Severity belongs to the Status column; Reason is a
 * category, so it is styled as one.
 */
export function ReasonBadge({ code }: { code: string | null }) {
  if (!code) return <span className="text-xs text-zinc-300">—</span>;
  return <Badge variant="neutral">{REASON_CODE_LABELS[code as ReasonCode] ?? code}</Badge>;
}

/** Generic label badge for free-text vendor states ("Available", "Special Order"). */
export function LabelBadge({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-xs text-zinc-300">—</span>;
  return <Badge variant="neutral">{titleCase(value)}</Badge>;
}
